import axios from 'axios'
import router from '@/router'

import { useAccount } from '@/stores/account'
import { useAuth } from '@/stores/auth'

let refreshPromise = null

function logout() {
  const account = useAccount()
  account.logout()
  return router.replace('/login')
}

export function errorInterceptor(error) {
  if (error.response && (error.response.status === 401 || error.response.data?.code === 'token_not_valid')) {
    const auth = useAuth()

    // If the user isn't logged in (e.g. failed login attempt), don't intercept — let the error propagate
    if (!auth.isLogged) {
      return Promise.reject(error)
    }

    // If the refresh endpoint itself returned 401, the refresh token is invalid — logout without retry.
    // Otherwise we'd loop: errorInterceptor → refreshPromise → refresh axios call → errorInterceptor → refreshPromise ...
    if (error.config?.url?.includes('/token/refresh')) {
      return logout()
    }

    if (auth.refresh) {
      // If a refresh is already in progress, wait on it instead of triggering another.
      // Resolves true on success, false if refresh failed (logout already invoked).
      if (!refreshPromise) {
        const account = useAccount()

        refreshPromise = account
          .refresh(auth.refresh)
          .then(() => true)
          .catch(() => {
            logout()
            return false
          })
          .finally(() => {
            refreshPromise = null
          })
      }

      // After refresh succeeds, retry the original request with the new token.
      // We must overwrite error.config.headers.Authorization explicitly: axios
      // merges defaults with per-request headers, and explicit per-request
      // headers win — so without this the retry would resend the EXPIRED
      // bearer that was baked into the original config, hit another 401, and
      // eventually log the user out.
      return refreshPromise.then((refreshed) => {
        if (!refreshed) {
          return Promise.reject(error)
        }
        if (error.config) {
          error.config.headers = error.config.headers || {}
          error.config.headers.Authorization = `Bearer ${auth.token}`
        }
        return axios(error.config)
      })
    } else {
      return logout()
    }
  }

  // Format the error to reject
  const data = error.response?.data?.data || null
  const errors = error.response?.data?.meta?.errors || []

  return Promise.reject({
    ...error,
    data,
    errors,
  })
}
